import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  generateBetaTesterCouponCode,
  generateEventCouponCode,
  generateGroupCouponCode,
  isBetaTesterPassCode,
  isEventPassCode,
} from '../../utils/groupPassCoupon';
import {
  loadEventIssuances,
  loadGroupIssuances,
  newAdminIssuedPassRowId,
  type AdminStoredEventPassRow,
  type AdminStoredGroupPassRow,
} from '../../utils/adminIssuedPassCodes';
import {
  deleteEventPassCodeRemote,
  deleteGroupPassCodeRemote,
  fetchEventPassCodesAdmin,
  fetchGroupPassCodesAdmin,
  importEventPassCodesFromLocalIfEmpty,
  importGroupPassCodesFromLocalIfEmpty,
  insertEventPassCode,
  insertGroupPassCode,
  updateEventPassCode,
  updateGroupPassCode,
} from '../../services/platformPassCodesService';
import {
  formatKoreanLongDate,
  formatYearMonthKo,
  planEndIsoFromStartMonthAndDurationMonths,
  toIsoDateLocal,
} from '../../utils/academicYearFebruaryEnd';
import {
  PAID_PLAN_MONTHLY_WON,
  PAID_PLAN_PARTIAL_DAY_RATE_WON,
  aggregatePaidPlanPricingSummaryLines,
  computePaidPlanPricing,
  formatKrw,
} from '../../utils/subscriptionPaidPlanPricing';

function simpleEmailValid(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function formatTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
}

/** 활성화한 이용자 수 / 코드에 허용된 전체 슬롯 */
function formatActivationRatio(activated: number, cap: number): string {
  const c = Math.max(1, Math.floor(cap));
  const a = Math.max(0, Math.min(Math.floor(activated), c));
  return `${a}/${c}`;
}

export interface AdminEventGroupPassIssuancePairProps {
  workspaceId: string;
  /** 2×2 배치 시 왼쪽 위(유료 이벤트 카드 등록 등) */
  leadingSlot?: React.ReactNode;
}

const AdminEventGroupPassIssuancePair: React.FC<AdminEventGroupPassIssuancePairProps> = ({
  workspaceId,
  leadingSlot,
}) => {
  const [allEventRows, setAllEventRows] = useState<AdminStoredEventPassRow[]>([]);
  const eventRows = useMemo(
    () => allEventRows.filter((r) => isEventPassCode(r.code)),
    [allEventRows],
  );
  const betaRows = useMemo(
    () => allEventRows.filter((r) => isBetaTesterPassCode(r.code)),
    [allEventRows],
  );

  const [eventName, setEventName] = useState('');
  const [startMonth, setStartMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [durationStr, setDurationStr] = useState('1');
  const [activeUserStr, setActiveUserStr] = useState('10');
  const [eventCopiedId, setEventCopiedId] = useState<string | null>(null);
  const [eventFormError, setEventFormError] = useState<string | null>(null);

  const [betaName, setBetaName] = useState('');
  const [betaStartMonth, setBetaStartMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [betaDurationStr, setBetaDurationStr] = useState('1');
  const [betaActiveUserStr, setBetaActiveUserStr] = useState('10');
  const [betaCopiedId, setBetaCopiedId] = useState<string | null>(null);
  const [betaFormError, setBetaFormError] = useState<string | null>(null);

  const [groupRows, setGroupRows] = useState<AdminStoredGroupPassRow[]>([]);
  const [groupName, setGroupName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [userCountStr, setUserCountStr] = useState('10');
  const [groupCopiedId, setGroupCopiedId] = useState<string | null>(null);
  const [groupFormError, setGroupFormError] = useState<string | null>(null);

  const [listModal, setListModal] = useState<'event' | 'group' | 'beta' | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editingPass, setEditingPass] = useState<{ kind: 'event' | 'beta'; id: string } | null>(
    null,
  );
  const [editName, setEditName] = useState('');
  const [editStartMonth, setEditStartMonth] = useState('');
  const [editDurationStr, setEditDurationStr] = useState('1');
  const [editActiveUserStr, setEditActiveUserStr] = useState('10');
  const [editError, setEditError] = useState<string | null>(null);

  const reloadEvents = useCallback(async () => {
    try {
      const local = loadEventIssuances(workspaceId);
      let rows = await fetchEventPassCodesAdmin();
      if (rows.length === 0 && local.length > 0) {
        rows = await importEventPassCodesFromLocalIfEmpty(local);
      }
      setAllEventRows(rows);
      setLoadError(null);
    } catch (err) {
      console.error(err);
      setLoadError('이벤트·베타 코드 목록을 DB에서 불러오지 못했습니다.');
      setAllEventRows(loadEventIssuances(workspaceId));
    }
  }, [workspaceId]);

  const reloadGroups = useCallback(async () => {
    try {
      const local = loadGroupIssuances(workspaceId);
      let rows = await fetchGroupPassCodesAdmin();
      if (rows.length === 0 && local.length > 0) {
        rows = await importGroupPassCodesFromLocalIfEmpty(local);
      }
      setGroupRows(rows);
      setLoadError(null);
    } catch (err) {
      console.error(err);
      setLoadError('그룹 이용권 코드 목록을 DB에서 불러오지 못했습니다.');
      setGroupRows(loadGroupIssuances(workspaceId));
    }
  }, [workspaceId]);

  useEffect(() => {
    void reloadEvents();
  }, [reloadEvents]);

  useEffect(() => {
    void reloadGroups();
  }, [reloadGroups]);

  useEffect(() => {
    if (!listModal && !editingPass) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editingPass) {
        setEditingPass(null);
        setEditError(null);
        return;
      }
      setListModal(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [listModal, editingPass]);

  const editingRow = useMemo(() => {
    if (!editingPass) return null;
    return allEventRows.find((r) => r.id === editingPass.id) ?? null;
  }, [editingPass, allEventRows]);

  const editDurationPreview = useMemo(() => {
    const rawD = Math.floor(Number(editDurationStr));
    return Math.min(36, Math.max(1, Number.isFinite(rawD) && rawD > 0 ? rawD : 1));
  }, [editDurationStr]);

  const editPreviewPlanEndIso = useMemo(
    () => planEndIsoFromStartMonthAndDurationMonths(editStartMonth, editDurationPreview),
    [editStartMonth, editDurationPreview],
  );

  const openEditPass = useCallback((row: AdminStoredEventPassRow, kind: 'event' | 'beta') => {
    setEditingPass({ kind, id: row.id });
    setEditName(row.eventName);
    setEditStartMonth(row.paidPlanStartMonth);
    setEditDurationStr(String(row.durationMonths));
    setEditActiveUserStr(String(row.activeUserCount));
    setEditError(null);
  }, []);

  const closeEditPass = useCallback(() => {
    setEditingPass(null);
    setEditError(null);
  }, []);

  const onSaveEditPass = useCallback(async () => {
    if (!editingPass || !editingRow) return;
    const name = editName.trim();
    if (!name) {
      setEditError(
        editingPass.kind === 'beta'
          ? '베타 테스터 코드 이름을 입력해 주세요.'
          : '이벤트 코드 이름을 입력해 주세요.',
      );
      return;
    }
    const rawD = Math.floor(Number(editDurationStr));
    const durationMonths = Math.min(36, Math.max(1, Number.isFinite(rawD) && rawD > 0 ? rawD : 1));
    const planEnd = planEndIsoFromStartMonthAndDurationMonths(editStartMonth.trim(), durationMonths);
    if (!planEnd) {
      setEditError('유료 플랜 시작 월과 이용 기간을 확인해 주세요.');
      return;
    }
    const rawU = Math.floor(Number(editActiveUserStr));
    const activeUserCount = Math.min(999, Math.max(1, Number.isFinite(rawU) && rawU > 0 ? rawU : 1));
    if (activeUserCount < editingRow.activatedUserCount) {
      setEditError(
        `이용자 수는 이미 활성화된 수(${editingRow.activatedUserCount})보다 작을 수 없습니다.`,
      );
      return;
    }
    setEditError(null);
    setBusy(true);
    try {
      await updateEventPassCode(editingPass.id, {
        eventName: name,
        paidPlanStartMonth: editStartMonth.trim(),
        durationMonths,
        planEndIso: planEnd,
        activeUserCount,
      });
      await reloadEvents();
      closeEditPass();
    } catch (err) {
      console.error(err);
      setEditError('수정을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }, [
    editingPass,
    editingRow,
    editName,
    editStartMonth,
    editDurationStr,
    editActiveUserStr,
    reloadEvents,
    closeEditPass,
  ]);

  const durationMonthsPreview = useMemo(() => {
    const rawD = Math.floor(Number(durationStr));
    return Math.min(36, Math.max(1, Number.isFinite(rawD) && rawD > 0 ? rawD : 1));
  }, [durationStr]);

  const previewPlanEndIso = useMemo(
    () => planEndIsoFromStartMonthAndDurationMonths(startMonth, durationMonthsPreview),
    [startMonth, durationMonthsPreview],
  );

  const betaDurationMonthsPreview = useMemo(() => {
    const rawD = Math.floor(Number(betaDurationStr));
    return Math.min(36, Math.max(1, Number.isFinite(rawD) && rawD > 0 ? rawD : 1));
  }, [betaDurationStr]);

  const betaPreviewPlanEndIso = useMemo(
    () => planEndIsoFromStartMonthAndDurationMonths(betaStartMonth, betaDurationMonthsPreview),
    [betaStartMonth, betaDurationMonthsPreview],
  );

  const groupUserCountPreview = useMemo(() => {
    const rawU = Math.floor(Number(userCountStr));
    return Math.min(999, Math.max(1, Number.isFinite(rawU) && rawU > 0 ? rawU : 1));
  }, [userCountStr]);

  const groupPricingPreview = useMemo(() => {
    const raw = startDate.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const [ys, ms, ds] = raw.split('-');
    const yy = Number(ys);
    const mm = Number(ms);
    const dd = Number(ds);
    if (!Number.isFinite(yy) || !Number.isFinite(mm) || !Number.isFinite(dd)) return null;
    const purchaseDate = new Date(yy, mm - 1, dd, 12, 0, 0, 0);
    if (Number.isNaN(purchaseDate.getTime())) return null;
    const billingStartFirstDay = new Date(yy, mm - 1, 1);
    try {
      return computePaidPlanPricing(billingStartFirstDay, purchaseDate);
    } catch {
      return null;
    }
  }, [startDate]);

  const groupEstimatedTotalWon = useMemo(() => {
    if (!groupPricingPreview) return 0;
    return groupPricingPreview.totalWon * groupUserCountPreview;
  }, [groupPricingPreview, groupUserCountPreview]);

  const onIssueEvent = useCallback(async () => {
    const name = eventName.trim();
    if (!name) {
      setEventFormError('이벤트 코드 이름을 입력해 주세요.');
      return;
    }
    const rawD = Math.floor(Number(durationStr));
    const durationMonths = Math.min(36, Math.max(1, Number.isFinite(rawD) && rawD > 0 ? rawD : 1));
    const planEnd = planEndIsoFromStartMonthAndDurationMonths(startMonth.trim(), durationMonths);
    if (!planEnd) {
      setEventFormError('유료 플랜 시작 월과 이용 기간을 확인해 주세요.');
      return;
    }
    setEventFormError(null);
    const rawU = Math.floor(Number(activeUserStr));
    const activeUserCount = Math.min(999, Math.max(1, Number.isFinite(rawU) && rawU > 0 ? rawU : 1));
    const issuedAt = new Date().toISOString();
    const paidPlanStartMonth = startMonth.trim();
    const row: AdminStoredEventPassRow = {
      id: newAdminIssuedPassRowId(),
      code: generateEventCouponCode(),
      issuedAt,
      eventName: name,
      paidPlanStartMonth,
      durationMonths,
      planEndIso: planEnd,
      activeUserCount,
      activatedUserCount: 0,
      saleStatus: 'active',
    };
    setBusy(true);
    try {
      await insertEventPassCode(row);
      setEventName('');
      await reloadEvents();
    } catch (err) {
      console.error(err);
      setEventFormError('이벤트 코드 발급을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }, [eventName, startMonth, durationStr, activeUserStr, reloadEvents]);

  const onIssueBeta = useCallback(async () => {
    const name = betaName.trim();
    if (!name) {
      setBetaFormError('베타 테스터 코드 이름을 입력해 주세요.');
      return;
    }
    const rawD = Math.floor(Number(betaDurationStr));
    const durationMonths = Math.min(36, Math.max(1, Number.isFinite(rawD) && rawD > 0 ? rawD : 1));
    const planEnd = planEndIsoFromStartMonthAndDurationMonths(betaStartMonth.trim(), durationMonths);
    if (!planEnd) {
      setBetaFormError('유료 플랜 시작 월과 이용 기간을 확인해 주세요.');
      return;
    }
    setBetaFormError(null);
    const rawU = Math.floor(Number(betaActiveUserStr));
    const activeUserCount = Math.min(999, Math.max(1, Number.isFinite(rawU) && rawU > 0 ? rawU : 1));
    const issuedAt = new Date().toISOString();
    const paidPlanStartMonth = betaStartMonth.trim();
    const row: AdminStoredEventPassRow = {
      id: newAdminIssuedPassRowId(),
      code: generateBetaTesterCouponCode(),
      issuedAt,
      eventName: name,
      paidPlanStartMonth,
      durationMonths,
      planEndIso: planEnd,
      activeUserCount,
      activatedUserCount: 0,
      saleStatus: 'active',
    };
    setBusy(true);
    try {
      await insertEventPassCode(row);
      setBetaName('');
      await reloadEvents();
    } catch (err) {
      console.error(err);
      setBetaFormError('베타 테스터 코드 발급을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }, [betaName, betaStartMonth, betaDurationStr, betaActiveUserStr, reloadEvents]);

  const onIssueGroup = useCallback(async () => {
    const gname = groupName.trim();
    if (!gname) {
      setGroupFormError('그룹명을 입력해 주세요.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate.trim())) {
      setGroupFormError('유료 플랜 시작일을 선택해 주세요.');
      return;
    }
    if (!simpleEmailValid(contactEmail)) {
      setGroupFormError('쿠폰·안내 메일을 받을 이메일 주소를 올바르게 입력해 주세요.');
      return;
    }
    setGroupFormError(null);
    const rawU = Math.floor(Number(userCountStr));
    const userCount = Math.min(999, Math.max(1, Number.isFinite(rawU) && rawU > 0 ? rawU : 1));
    const issuedAt = new Date().toISOString();
    const paidPlanStartDate = startDate.trim();
    const email = contactEmail.trim();
    const row: AdminStoredGroupPassRow = {
      id: newAdminIssuedPassRowId(),
      code: generateGroupCouponCode(),
      issuedAt,
      groupName: gname,
      contactEmail: email,
      paidPlanStartDate,
      userCount,
      activatedUserCount: 0,
      orderStatus: 'issued',
      refundedAt: null,
      lastResentAt: null,
    };
    setBusy(true);
    try {
      await insertGroupPassCode(row);
      setGroupName('');
      await reloadGroups();
    } catch (err) {
      console.error(err);
      setGroupFormError('그룹 이용권 코드 발급을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }, [groupName, contactEmail, startDate, userCountStr, reloadGroups]);

  const onCopyEvent = useCallback(async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setEventCopiedId(id);
      window.setTimeout(() => setEventCopiedId(null), 2000);
    } catch {
      setEventCopiedId(null);
    }
  }, []);

  const onCopyBeta = useCallback(async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setBetaCopiedId(id);
      window.setTimeout(() => setBetaCopiedId(null), 2000);
    } catch {
      setBetaCopiedId(null);
    }
  }, []);

  const onCopyGroup = useCallback(async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setGroupCopiedId(id);
      window.setTimeout(() => setGroupCopiedId(null), 2000);
    } catch {
      setGroupCopiedId(null);
    }
  }, []);

  const onEventSaleClose = useCallback(
    async (id: string) => {
      setBusy(true);
      try {
        await updateEventPassCode(id, { saleStatus: 'closed' });
        await reloadEvents();
      } catch (err) {
        console.error(err);
        window.alert('판매 마감 처리에 실패했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [reloadEvents],
  );

  const onEventSaleResume = useCallback(
    async (id: string) => {
      setBusy(true);
      try {
        await updateEventPassCode(id, { saleStatus: 'active' });
        await reloadEvents();
      } catch (err) {
        console.error(err);
        window.alert('판매 재개 처리에 실패했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [reloadEvents],
  );

  const onEventDelete = useCallback(
    async (id: string, kind: 'event' | 'beta' = 'event') => {
      const label = kind === 'beta' ? '베타 테스터 코드' : '이벤트 코드';
      if (!window.confirm(`이 ${label} 발급 건을 삭제할까요? 되돌릴 수 없습니다.`)) return;
      setBusy(true);
      try {
        await deleteEventPassCodeRemote(id);
        await reloadEvents();
      } catch (err) {
        console.error(err);
        window.alert('삭제에 실패했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [reloadEvents],
  );

  const onGroupResend = useCallback(
    async (row: AdminStoredGroupPassRow) => {
      const email = row.contactEmail.trim() || '(이메일 없음)';
      window.alert(
        `다음 주소로 그룹 이용권 코드 재발송을 기록합니다.\n\n수신: ${email}\n코드: ${row.code}\n\n실제 메일 발송은 별도 연동 후 가능합니다.`,
      );
      setBusy(true);
      try {
        await updateGroupPassCode(row.id, { lastResentAt: new Date().toISOString() });
        await reloadGroups();
      } catch (err) {
        console.error(err);
        window.alert('재발송 기록 저장에 실패했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [reloadGroups],
  );

  const onGroupRefund = useCallback(
    async (row: AdminStoredGroupPassRow) => {
      if (row.orderStatus === 'refunded') return;
      if (
        !window.confirm(
          `'${row.groupName}' 건을 환불 완료로 처리할까요?\n(목록 상태가 바뀌며, 이후 코드 등록이 막힙니다.)`,
        )
      ) {
        return;
      }
      setBusy(true);
      try {
        await updateGroupPassCode(row.id, {
          orderStatus: 'refunded',
          refundedAt: new Date().toISOString(),
        });
        await reloadGroups();
      } catch (err) {
        console.error(err);
        window.alert('환불 상태 저장에 실패했습니다.');
      } finally {
        setBusy(false);
      }
    },
    [reloadGroups],
  );

  return (
    <>
      <div
        className={
          'cp-admin-users-pass-issue-pair cp-admin-users-pass-issue-pair--compact' +
          (leadingSlot ? ' cp-admin-users-pass-issue-pair--quadro' : '')
        }
      >
      {leadingSlot}
      <article
        className="cp-dashboard-block cp-admin-users-pass-block cp-admin-users-pass-issue-pair-col"
        aria-labelledby="cp-admin-users-pass-beta-head"
      >
        <h3 className="cp-dashboard-block__head" id="cp-admin-users-pass-beta-head">
          <span className="cp-dashboard-block__head-title">베타 테스터 이용권 코드 생성 및 발급 관리</span>
        </h3>
        <div className="cp-admin-users-pass-issue-pair-intro">
          {loadError ? (
            <p className="cp-admin-users-pass-issue-form-error" role="alert">
              {loadError}
            </p>
          ) : null}
        </div>
        <div className="cp-dashboard-block__body cp-admin-users-pass-block-body">
          <div className="cp-admin-users-pass-issue-form cp-admin-users-pass-issue-form--stack">
            <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-pass-slot cp-admin-pass-slot--name">
              <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-cbt-name">
                베타 테스터 코드 이름
              </label>
              <input
                id="cp-admin-pass-cbt-name"
                className="cp-admin-users-pass-issue-input"
                type="text"
                maxLength={200}
                placeholder="예: 2026년 베타 1차"
                value={betaName}
                onChange={(e) => setBetaName(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="cp-admin-users-pass-issuance-email-slot cp-admin-pass-slot cp-admin-pass-slot--secondary" aria-hidden="true">
              <span className="cp-admin-users-pass-issue-label cp-admin-users-pass-issue-label--rivet-hidden">
                &nbsp;
              </span>
              <div className="cp-admin-users-pass-issuance-email-slot-box" />
            </div>
            <div className="cp-admin-users-pass-issue-pair-fields cp-admin-users-pass-issue-pair-fields--plan-band cp-admin-pass-slot cp-admin-pass-slot--plan">
              <div className="cp-admin-users-pass-issue-field">
                <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-cbt-start-month">
                  유료 플랜 시작 월
                </label>
                <input
                  id="cp-admin-pass-cbt-start-month"
                  className="cp-admin-users-pass-issue-input"
                  type="month"
                  value={betaStartMonth}
                  onChange={(e) => setBetaStartMonth(e.target.value)}
                />
              </div>
              <div className="cp-admin-users-pass-issue-field">
                <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-cbt-duration">
                  이용 기간(개월)
                </label>
                <input
                  id="cp-admin-pass-cbt-duration"
                  className="cp-admin-users-pass-issue-input"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={36}
                  value={betaDurationStr}
                  onChange={(e) => setBetaDurationStr(e.target.value)}
                />
              </div>
            </div>
            <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-pass-slot cp-admin-pass-slot--users">
              <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-cbt-users">
                이용자 수
              </label>
              <input
                id="cp-admin-pass-cbt-users"
                className="cp-admin-users-pass-issue-input"
                type="number"
                inputMode="numeric"
                min={1}
                max={999}
                value={betaActiveUserStr}
                onChange={(e) => setBetaActiveUserStr(e.target.value)}
              />
            </div>
            <div className="cp-admin-users-pass-issuance-preview-band cp-admin-pass-slot cp-admin-pass-slot--preview">
              {betaPreviewPlanEndIso && /^\d{4}-\d{2}$/.test(betaStartMonth.trim()) ? (
                <p className="cp-admin-users-pass-issue-fixed-end">
                  이용권 이용일:{' '}
                  <strong>{formatKoreanLongDate(`${betaStartMonth.trim()}-01`)}</strong>
                  {' ~ '}
                  <strong>{formatKoreanLongDate(betaPreviewPlanEndIso)}</strong>
                </p>
              ) : (
                <p className="cp-admin-users-pass-issuance-preview-placeholder">
                  유료 플랜 시작 월과 이용 기간을 입력하면 이용일이 표시됩니다.
                </p>
              )}
              {betaFormError ? (
                <p className="cp-admin-users-pass-issue-form-error" role="alert">
                  {betaFormError}
                </p>
              ) : null}
            </div>
            <div className="cp-admin-users-pass-issue-actions cp-admin-users-pass-issue-actions--block cp-admin-pass-slot cp-admin-pass-slot--actions">
              <button
                type="button"
                className="cp-btn-primary cp-admin-users-pass-issue-create-btn"
                onClick={() => void onIssueBeta()}
                disabled={busy}
              >
                베타 테스터 코드 생성
              </button>
            </div>
          </div>
          <div className="cp-admin-users-pass-issue-list-open cp-admin-pass-slot cp-admin-pass-slot--list">
            <button
              type="button"
              className="cp-btn-secondary cp-admin-users-pass-issue-list-open-btn"
              onClick={() => setListModal('beta')}
            >
              베타 테스터 코드 목록
              {betaRows.length > 0 ? ` (${betaRows.length})` : ''}
            </button>
          </div>
        </div>
      </article>

      <article
        className="cp-dashboard-block cp-admin-users-pass-block cp-admin-users-pass-issue-pair-col"
        aria-labelledby="cp-admin-users-pass-event-head"
      >
        <h3 className="cp-dashboard-block__head" id="cp-admin-users-pass-event-head">
          <span className="cp-dashboard-block__head-title">이벤트 이용권 코드 생성 및 발급 관리</span>
        </h3>
        <div className="cp-admin-users-pass-issue-pair-intro">
          {loadError ? (
            <p className="cp-admin-users-pass-issue-form-error" role="alert">
              {loadError}
            </p>
          ) : null}
        </div>
        <div className="cp-dashboard-block__body cp-admin-users-pass-block-body">
          <div className="cp-admin-users-pass-issue-form cp-admin-users-pass-issue-form--stack">
            <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-pass-slot cp-admin-pass-slot--name">
              <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-evt-name">
                이벤트 코드 이름
              </label>
              <input
                id="cp-admin-pass-evt-name"
                className="cp-admin-users-pass-issue-input"
                type="text"
                maxLength={200}
                placeholder="예: 2026년 1월 이벤트"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="cp-admin-users-pass-issuance-email-slot cp-admin-pass-slot cp-admin-pass-slot--secondary" aria-hidden="true">
              <span className="cp-admin-users-pass-issue-label cp-admin-users-pass-issue-label--rivet-hidden">
                &nbsp;
              </span>
              <div className="cp-admin-users-pass-issuance-email-slot-box" />
            </div>
            <div className="cp-admin-users-pass-issue-pair-fields cp-admin-users-pass-issue-pair-fields--plan-band cp-admin-pass-slot cp-admin-pass-slot--plan">
              <div className="cp-admin-users-pass-issue-field">
                <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-evt-start-month">
                  유료 플랜 시작 월
                </label>
                <input
                  id="cp-admin-pass-evt-start-month"
                  className="cp-admin-users-pass-issue-input"
                  type="month"
                  value={startMonth}
                  onChange={(e) => setStartMonth(e.target.value)}
                />
              </div>
              <div className="cp-admin-users-pass-issue-field">
                <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-evt-duration">
                  이용 기간(개월)
                </label>
                <input
                  id="cp-admin-pass-evt-duration"
                  className="cp-admin-users-pass-issue-input"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={36}
                  value={durationStr}
                  onChange={(e) => setDurationStr(e.target.value)}
                />
              </div>
            </div>
            <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-pass-slot cp-admin-pass-slot--users">
              <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-evt-users">
                이용자 수
              </label>
              <input
                id="cp-admin-pass-evt-users"
                className="cp-admin-users-pass-issue-input"
                type="number"
                inputMode="numeric"
                min={1}
                max={999}
                value={activeUserStr}
                onChange={(e) => setActiveUserStr(e.target.value)}
              />
            </div>
            <div className="cp-admin-users-pass-issuance-preview-band cp-admin-pass-slot cp-admin-pass-slot--preview">
              {previewPlanEndIso && /^\d{4}-\d{2}$/.test(startMonth.trim()) ? (
                <p className="cp-admin-users-pass-issue-fixed-end">
                  이용권 이용일:{' '}
                  <strong>{formatKoreanLongDate(`${startMonth.trim()}-01`)}</strong>
                  {' ~ '}
                  <strong>{formatKoreanLongDate(previewPlanEndIso)}</strong>
                </p>
              ) : (
                <p className="cp-admin-users-pass-issuance-preview-placeholder">
                  유료 플랜 시작 월과 이용 기간을 입력하면 이용일이 표시됩니다.
                </p>
              )}
              {eventFormError ? (
                <p className="cp-admin-users-pass-issue-form-error" role="alert">
                  {eventFormError}
                </p>
              ) : null}
            </div>
            <div className="cp-admin-users-pass-issue-actions cp-admin-users-pass-issue-actions--block cp-admin-pass-slot cp-admin-pass-slot--actions">
              <button
                type="button"
                className="cp-btn-primary cp-admin-users-pass-issue-create-btn"
                onClick={() => void onIssueEvent()}
                disabled={busy}
              >
                이벤트 코드 생성
              </button>
            </div>
          </div>
          <div className="cp-admin-users-pass-issue-list-open cp-admin-pass-slot cp-admin-pass-slot--list">
            <button
              type="button"
              className="cp-btn-secondary cp-admin-users-pass-issue-list-open-btn"
              onClick={() => setListModal('event')}
            >
              이벤트 코드 목록
              {eventRows.length > 0 ? ` (${eventRows.length})` : ''}
            </button>
          </div>
        </div>
      </article>

      <article
        className="cp-dashboard-block cp-admin-users-pass-block cp-admin-users-pass-issue-pair-col"
        aria-labelledby="cp-admin-users-pass-group-head"
      >
        <h3 className="cp-dashboard-block__head" id="cp-admin-users-pass-group-head">
          <span className="cp-dashboard-block__head-title">그룹 이용권 코드 생성 및 발급 관리</span>
        </h3>
        <div className="cp-admin-users-pass-issue-pair-intro">
          {loadError ? (
            <p className="cp-admin-users-pass-issue-form-error" role="alert">
              {loadError}
            </p>
          ) : null}
        </div>
        <div className="cp-dashboard-block__body cp-admin-users-pass-block-body">
          <div className="cp-admin-users-pass-issue-form cp-admin-users-pass-issue-form--stack">
            <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-pass-slot cp-admin-pass-slot--name">
              <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-grp-name">
                그룹명
              </label>
              <input
                id="cp-admin-pass-grp-name"
                className="cp-admin-users-pass-issue-input"
                type="text"
                maxLength={200}
                placeholder="예: 서울 캠퍼스 A반"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                autoComplete="organization"
              />
            </div>
            <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-pass-slot cp-admin-pass-slot--secondary">
              <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-grp-email">
                이메일
              </label>
              <input
                id="cp-admin-pass-grp-email"
                className="cp-admin-users-pass-issue-input"
                type="email"
                inputMode="email"
                maxLength={200}
                placeholder="example@email.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-users-pass-issue-field--plan-band cp-admin-pass-slot cp-admin-pass-slot--plan">
              <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-grp-start">
                유료 플랜 시작일
              </label>
              <input
                id="cp-admin-pass-grp-start"
                className="cp-admin-users-pass-issue-input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full cp-admin-pass-slot cp-admin-pass-slot--users">
              <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-grp-users">
                이용자 수
              </label>
              <input
                id="cp-admin-pass-grp-users"
                className="cp-admin-users-pass-issue-input"
                type="number"
                inputMode="numeric"
                min={1}
                max={999}
                value={userCountStr}
                onChange={(e) => setUserCountStr(e.target.value)}
              />
            </div>
            <div className="cp-admin-users-pass-issuance-preview-band cp-admin-pass-slot cp-admin-pass-slot--preview">
              {groupPricingPreview ? (
                <>
                  <p className="cp-admin-users-pass-issue-fixed-end">
                    이용권 이용일:{' '}
                    <strong>{formatKoreanLongDate(toIsoDateLocal(groupPricingPreview.periodStart))}</strong>
                    {' ~ '}
                    <strong>
                      {formatKoreanLongDate(toIsoDateLocal(groupPricingPreview.billedPeriodEndInclusive))}
                    </strong>
                  </p>
                  <p
                    className="cp-sub-pass-breakdown-section-title"
                    id="cp-admin-pass-grp-breakdown-title"
                  >
                    결제금액 산출 내역
                  </p>
                  <div
                    className="cp-sub-pass-pricing-lines-text cp-admin-users-pass-issuance-pricing-box"
                    role="region"
                    aria-labelledby="cp-admin-pass-grp-breakdown-title"
                  >
                    {aggregatePaidPlanPricingSummaryLines(groupPricingPreview).map((line, i) => (
                      <p key={`admin-grp-price-line-${i}`} className="cp-sub-pass-pricing-lines-text-line">
                        {line}
                      </p>
                    ))}
                    <p className="cp-sub-pass-pricing-lines-text-line">
                      1인당 {formatKrw(groupPricingPreview.totalWon)}원 × {groupUserCountPreview}명 ={' '}
                      {formatKrw(groupEstimatedTotalWon)}원
                    </p>
                    <p className="cp-sub-pass-pricing-lines-text-foot">
                      월 {formatKrw(PAID_PLAN_MONTHLY_WON)}원 · 일 {formatKrw(PAID_PLAN_PARTIAL_DAY_RATE_WON)}
                      원 (한 달 최대 {formatKrw(PAID_PLAN_MONTHLY_WON)}원)
                    </p>
                  </div>
                </>
              ) : (
                <p className="cp-admin-users-pass-issuance-preview-placeholder">
                  유료 플랜 시작일을 선택하면 이용 구간·예상 금액이 표시됩니다.
                </p>
              )}
              {groupFormError ? (
                <p className="cp-admin-users-pass-issue-form-error" role="alert">
                  {groupFormError}
                </p>
              ) : null}
            </div>
            <div className="cp-admin-users-pass-issue-actions cp-admin-users-pass-issue-actions--block cp-admin-pass-slot cp-admin-pass-slot--actions">
              <button
                type="button"
                className="cp-btn-primary cp-admin-users-pass-issue-create-btn"
                onClick={() => void onIssueGroup()}
                disabled={busy}
              >
                그룹 이용권 생성
              </button>
            </div>
          </div>
          <div className="cp-admin-users-pass-issue-list-open cp-admin-pass-slot cp-admin-pass-slot--list">
            <button
              type="button"
              className="cp-btn-secondary cp-admin-users-pass-issue-list-open-btn"
              onClick={() => setListModal('group')}
            >
              그룹 이용권 목록
              {groupRows.length > 0 ? ` (${groupRows.length})` : ''}
            </button>
          </div>
        </div>
      </article>
      </div>

      {listModal === 'beta' ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onClick={() => {
            if (editingPass) return;
            setListModal(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !editingPass) setListModal(null);
          }}
        >
          <div
            className="cp-modal cp-admin-users-pass-detail-modal cp-admin-users-pass-issuance-list-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-pass-cbt-list-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-pass-cbt-list-title">베타 테스터 코드 목록</h3>
            <div className="cp-modal-body cp-admin-users-pass-detail-modal-body">
              {betaRows.length === 0 ? (
                <p className="cp-admin-users-pass-modal-empty" role="status">
                  아직 발급한 베타 테스터 코드가 없습니다.
                </p>
              ) : (
                <div className="cp-admin-users-pass-issue-table-wrap" tabIndex={0}>
                  <table className="cp-admin-users-pass-issue-table cp-admin-users-pass-issue-table--modal">
                    <thead>
                      <tr>
                        <th scope="col">발급일시</th>
                        <th scope="col">코드</th>
                        <th scope="col">이름</th>
                        <th scope="col">시작월</th>
                        <th scope="col">기간(월)</th>
                        <th scope="col">이용권 종료</th>
                        <th scope="col">이용자</th>
                        <th scope="col">상태</th>
                        <th scope="col">마감·재개</th>
                        <th scope="col">수정</th>
                        <th scope="col">삭제</th>
                      </tr>
                    </thead>
                    <tbody>
                      {betaRows.map((r) => (
                        <tr key={r.id}>
                          <td>{formatTs(r.issuedAt)}</td>
                          <td className="cp-admin-users-pass-issue-code-cell">
                            <div className="cp-admin-users-pass-issue-code-with-copy">
                              <code className="cp-admin-users-pass-issue-code">{r.code}</code>
                              <button
                                type="button"
                                className="cp-btn-secondary cp-admin-users-pass-issue-copy-btn cp-admin-users-pass-issue-copy-btn-inline"
                                onClick={() => void onCopyBeta(r.code, r.id)}
                              >
                                {betaCopiedId === r.id ? '복사됨' : '복사'}
                              </button>
                            </div>
                          </td>
                          <td className="cp-admin-users-pass-issue-memo">{r.eventName}</td>
                          <td>{formatYearMonthKo(r.paidPlanStartMonth)}</td>
                          <td className="cp-admin-users-pass-issue-num">{r.durationMonths}</td>
                          <td>{formatKoreanLongDate(r.planEndIso)}</td>
                          <td className="cp-admin-users-pass-issue-num cp-admin-users-pass-issue-activation-mono">
                            {formatActivationRatio(r.activatedUserCount, r.activeUserCount)}
                          </td>
                          <td>
                            <span
                              className={
                                'cp-admin-users-pass-issue-status-pill' +
                                (r.saleStatus === 'closed'
                                  ? ' cp-admin-users-pass-issue-status-pill--muted'
                                  : ' cp-admin-users-pass-issue-status-pill--ok')
                              }
                            >
                              {r.saleStatus === 'closed' ? '마감' : '활성'}
                            </span>
                          </td>
                          <td>
                            <div className="cp-admin-users-pass-issue-table-actions">
                              {r.saleStatus === 'active' ? (
                                <button
                                  type="button"
                                  className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn"
                                  onClick={() => onEventSaleClose(r.id)}
                                >
                                  마감
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn"
                                  onClick={() => onEventSaleResume(r.id)}
                                >
                                  재개
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn"
                              onClick={() => openEditPass(r, 'beta')}
                            >
                              수정
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn cp-admin-users-pass-issue-table-action-btn--danger"
                              onClick={() => onEventDelete(r.id, 'beta')}
                            >
                              삭제
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setListModal(null)}
                disabled={!!editingPass}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {listModal === 'event' ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onClick={() => {
            if (editingPass) return;
            setListModal(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !editingPass) setListModal(null);
          }}
        >
          <div
            className="cp-modal cp-admin-users-pass-detail-modal cp-admin-users-pass-issuance-list-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-pass-evt-list-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-pass-evt-list-title">이벤트 코드 목록</h3>
            <div className="cp-modal-body cp-admin-users-pass-detail-modal-body">
              {eventRows.length === 0 ? (
                <p className="cp-admin-users-pass-modal-empty" role="status">
                  아직 발급한 이벤트 코드가 없습니다.
                </p>
              ) : (
                <div className="cp-admin-users-pass-issue-table-wrap" tabIndex={0}>
                  <table className="cp-admin-users-pass-issue-table cp-admin-users-pass-issue-table--modal">
                    <thead>
                      <tr>
                        <th scope="col">발급일시</th>
                        <th scope="col">코드</th>
                        <th scope="col">이벤트명</th>
                        <th scope="col">시작월</th>
                        <th scope="col">기간(월)</th>
                        <th scope="col">이용권 종료</th>
                        <th scope="col">이용자</th>
                        <th scope="col">상태</th>
                        <th scope="col">마감·재개</th>
                        <th scope="col">수정</th>
                        <th scope="col">삭제</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventRows.map((r) => (
                        <tr key={r.id}>
                          <td>{formatTs(r.issuedAt)}</td>
                          <td className="cp-admin-users-pass-issue-code-cell">
                            <div className="cp-admin-users-pass-issue-code-with-copy">
                              <code className="cp-admin-users-pass-issue-code">{r.code}</code>
                              <button
                                type="button"
                                className="cp-btn-secondary cp-admin-users-pass-issue-copy-btn cp-admin-users-pass-issue-copy-btn-inline"
                                onClick={() => void onCopyEvent(r.code, r.id)}
                              >
                                {eventCopiedId === r.id ? '복사됨' : '복사'}
                              </button>
                            </div>
                          </td>
                          <td className="cp-admin-users-pass-issue-memo">{r.eventName}</td>
                          <td>{formatYearMonthKo(r.paidPlanStartMonth)}</td>
                          <td className="cp-admin-users-pass-issue-num">{r.durationMonths}</td>
                          <td>{formatKoreanLongDate(r.planEndIso)}</td>
                          <td className="cp-admin-users-pass-issue-num cp-admin-users-pass-issue-activation-mono">
                            {formatActivationRatio(r.activatedUserCount, r.activeUserCount)}
                          </td>
                          <td>
                            <span
                              className={
                                'cp-admin-users-pass-issue-status-pill' +
                                (r.saleStatus === 'closed'
                                  ? ' cp-admin-users-pass-issue-status-pill--muted'
                                  : ' cp-admin-users-pass-issue-status-pill--ok')
                              }
                            >
                              {r.saleStatus === 'closed' ? '마감' : '활성'}
                            </span>
                          </td>
                          <td>
                            <div className="cp-admin-users-pass-issue-table-actions">
                              {r.saleStatus === 'active' ? (
                                <button
                                  type="button"
                                  className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn"
                                  onClick={() => onEventSaleClose(r.id)}
                                >
                                  마감
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn"
                                  onClick={() => onEventSaleResume(r.id)}
                                >
                                  재개
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn"
                              onClick={() => openEditPass(r, 'event')}
                            >
                              수정
                            </button>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn cp-admin-users-pass-issue-table-action-btn--danger"
                              onClick={() => onEventDelete(r.id)}
                            >
                              삭제
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setListModal(null)}
                disabled={!!editingPass}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {listModal === 'group' ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onClick={() => setListModal(null)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setListModal(null);
          }}
        >
          <div
            className="cp-modal cp-admin-users-pass-detail-modal cp-admin-users-pass-issuance-list-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-pass-grp-list-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-pass-grp-list-title">그룹 이용권 목록</h3>
            <div className="cp-modal-body cp-admin-users-pass-detail-modal-body">
              {groupRows.length === 0 ? (
                <p className="cp-admin-users-pass-modal-empty" role="status">
                  아직 발급한 그룹 코드가 없습니다.
                </p>
              ) : (
                <div className="cp-admin-users-pass-issue-table-wrap" tabIndex={0}>
                  <table className="cp-admin-users-pass-issue-table cp-admin-users-pass-issue-table--modal">
                    <thead>
                      <tr>
                        <th scope="col">발급일시</th>
                        <th scope="col">코드</th>
                        <th scope="col">그룹명</th>
                        <th scope="col">이메일</th>
                        <th scope="col">시작일</th>
                        <th scope="col">이용자</th>
                        <th scope="col">상태</th>
                        <th scope="col">코드 재발송</th>
                        <th scope="col">환불</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupRows.map((r) => (
                        <tr key={r.id}>
                          <td>{formatTs(r.issuedAt)}</td>
                          <td className="cp-admin-users-pass-issue-code-cell">
                            <div className="cp-admin-users-pass-issue-code-with-copy">
                              <code className="cp-admin-users-pass-issue-code">{r.code}</code>
                              <button
                                type="button"
                                className="cp-btn-secondary cp-admin-users-pass-issue-copy-btn cp-admin-users-pass-issue-copy-btn-inline"
                                onClick={() => void onCopyGroup(r.code, r.id)}
                              >
                                {groupCopiedId === r.id ? '복사됨' : '복사'}
                              </button>
                            </div>
                          </td>
                          <td className="cp-admin-users-pass-issue-memo">{r.groupName}</td>
                          <td className="cp-admin-users-pass-issue-memo" title={r.contactEmail || undefined}>
                            {r.contactEmail || '—'}
                          </td>
                          <td>{formatKoreanLongDate(r.paidPlanStartDate)}</td>
                          <td className="cp-admin-users-pass-issue-num cp-admin-users-pass-issue-activation-mono">
                            {formatActivationRatio(r.activatedUserCount, r.userCount)}
                          </td>
                          <td>
                            <span
                              className={
                                'cp-admin-users-pass-issue-status-pill' +
                                (r.orderStatus === 'refunded'
                                  ? ' cp-admin-users-pass-issue-status-pill--muted'
                                  : ' cp-admin-users-pass-issue-status-pill--ok')
                              }
                            >
                              {r.orderStatus === 'refunded' ? '환불완료' : '발행'}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn"
                              onClick={() => onGroupResend(r)}
                            >
                              재발송
                            </button>
                          </td>
                          <td>
                            {r.orderStatus === 'refunded' ? (
                              <span className="cp-admin-users-pass-issue-table-muted">—</span>
                            ) : (
                              <button
                                type="button"
                                className="cp-btn-secondary cp-admin-users-pass-issue-table-action-btn cp-admin-users-pass-issue-table-action-btn--danger"
                                onClick={() => onGroupRefund(r)}
                              >
                                환불
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setListModal(null)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editingPass && editingRow ? (
        <div className="cp-modal-backdrop" role="presentation" onClick={closeEditPass}>
          <div
            className="cp-modal cp-admin-users-pass-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-pass-code-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-pass-code-edit-title">
              {editingPass.kind === 'beta' ? '베타 테스터 코드 수정' : '이벤트 코드 수정'}
            </h3>
            <div className="cp-modal-body cp-admin-users-pass-detail-modal-body">
              <div className="cp-admin-users-pass-issue-form cp-admin-users-pass-issue-form--stack">
                <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full">
                  <span className="cp-admin-users-pass-issue-label">코드</span>
                  <code className="cp-admin-users-pass-issue-code">{editingRow.code}</code>
                </div>
                <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full">
                  <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-edit-name">
                    {editingPass.kind === 'beta' ? '베타 테스터 코드 이름' : '이벤트 코드 이름'}
                  </label>
                  <input
                    id="cp-admin-pass-edit-name"
                    className="cp-admin-users-pass-issue-input"
                    type="text"
                    maxLength={200}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="cp-admin-users-pass-issue-pair-fields">
                  <div className="cp-admin-users-pass-issue-field">
                    <label
                      className="cp-admin-users-pass-issue-label"
                      htmlFor="cp-admin-pass-edit-start-month"
                    >
                      유료 플랜 시작 월
                    </label>
                    <input
                      id="cp-admin-pass-edit-start-month"
                      className="cp-admin-users-pass-issue-input"
                      type="month"
                      value={editStartMonth}
                      onChange={(e) => setEditStartMonth(e.target.value)}
                    />
                  </div>
                  <div className="cp-admin-users-pass-issue-field">
                    <label
                      className="cp-admin-users-pass-issue-label"
                      htmlFor="cp-admin-pass-edit-duration"
                    >
                      이용 기간(개월)
                    </label>
                    <input
                      id="cp-admin-pass-edit-duration"
                      className="cp-admin-users-pass-issue-input"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={36}
                      value={editDurationStr}
                      onChange={(e) => setEditDurationStr(e.target.value)}
                    />
                  </div>
                </div>
                <div className="cp-admin-users-pass-issue-field cp-admin-users-pass-issue-field--full">
                  <label className="cp-admin-users-pass-issue-label" htmlFor="cp-admin-pass-edit-users">
                    이용자 수
                  </label>
                  <input
                    id="cp-admin-pass-edit-users"
                    className="cp-admin-users-pass-issue-input"
                    type="number"
                    inputMode="numeric"
                    min={Math.max(1, editingRow.activatedUserCount)}
                    max={999}
                    value={editActiveUserStr}
                    onChange={(e) => setEditActiveUserStr(e.target.value)}
                  />
                  <p className="cp-admin-users-pass-issuance-preview-placeholder" style={{ marginTop: 6 }}>
                    현재 활성화 {editingRow.activatedUserCount}명 — 이용자 수는 이보다 작을 수 없습니다.
                  </p>
                </div>
                <div className="cp-admin-users-pass-issuance-preview-band">
                  {editPreviewPlanEndIso && /^\d{4}-\d{2}$/.test(editStartMonth.trim()) ? (
                    <p className="cp-admin-users-pass-issue-fixed-end">
                      이용권 이용일:{' '}
                      <strong>{formatKoreanLongDate(`${editStartMonth.trim()}-01`)}</strong>
                      {' ~ '}
                      <strong>{formatKoreanLongDate(editPreviewPlanEndIso)}</strong>
                    </p>
                  ) : (
                    <p className="cp-admin-users-pass-issuance-preview-placeholder">
                      유료 플랜 시작 월과 이용 기간을 입력하면 이용일이 표시됩니다.
                    </p>
                  )}
                </div>
                {editError ? (
                  <p className="cp-admin-users-pass-issue-form-error" role="alert">
                    {editError}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={closeEditPass} disabled={busy}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void onSaveEditPass()}
                disabled={busy}
              >
                {busy ? '저장 중…' : '저장'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AdminEventGroupPassIssuancePair;
